import type { TaxType } from "../api/hooks";
import type { BillingFormValues } from "../lib/tenant-fiscal";
import { Field, Input, Select } from "./ui";

const TAX_TYPE_HINT =
  "Impuesto que se SUMA al precio de sus facturas (los precios de los planes son siempre sin impuestos). Los tipos y sus porcentajes se gestionan en «Tipos fiscales».";

/** Etiqueta del selector: el nombre del tipo y su porcentaje. */
function taxTypeOptionLabel(taxType: TaxType): string {
  return `${taxType.name} — ${Number(taxType.percentage)} %${taxType.isActive ? "" : " (inactivo)"}`;
}

/**
 * Campos de la pestaña «Facturación» del tenant (alta y gestión): razón social, domicilio fiscal,
 * código de cliente del ERP, correo de facturación y tipo fiscal. Son opcionales hasta que se asigna
 * un plan; entonces la API los exige completos.
 */
export function TenantBillingFields({
  idPrefix,
  values,
  onChange,
  taxTypes,
}: {
  idPrefix: string;
  values: BillingFormValues;
  onChange: (next: BillingFormValues) => void;
  taxTypes: TaxType[];
}) {
  const set = (key: keyof BillingFormValues) => (event: { target: { value: string } }) =>
    onChange({ ...values, [key]: event.target.value });

  return (
    <div className="space-y-3">
      <p className="text-sm text-ink-600 dark:text-ink-300">
        Datos que salen en la factura. Son opcionales hasta que se asigna un plan: entonces deben estar completos.
        Razón social, domicilio y NIF se envían a Stripe (solo en ese sentido).
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field
          label="Razón social"
          htmlFor={`${idPrefix}-legal-name`}
          hint="Vacío = se usa el nombre del tenant."
        >
          <Input id={`${idPrefix}-legal-name`} value={values.legalName} onChange={set("legalName")} />
        </Field>
        <Field
          label="Código de cliente del ERP"
          htmlFor={`${idPrefix}-erp-code`}
          hint="Debe coincidir con el código del cliente en el ERP. Único; no se genera solo."
        >
          <Input
            id={`${idPrefix}-erp-code`}
            value={values.erpCustomerCode}
            onChange={set("erpCustomerCode")}
            maxLength={20}
            placeholder="1"
          />
        </Field>
      </div>
      <Field label="Domicilio" htmlFor={`${idPrefix}-address`}>
        <Input
          id={`${idPrefix}-address`}
          value={values.billingAddress}
          onChange={set("billingAddress")}
          placeholder="Calle Mayor 1"
        />
      </Field>
      <div className="grid gap-3 sm:grid-cols-4">
        <Field label="Código postal" htmlFor={`${idPrefix}-postal`}>
          <Input
            id={`${idPrefix}-postal`}
            value={values.billingPostalCode}
            onChange={set("billingPostalCode")}
            maxLength={15}
            placeholder="28001"
          />
        </Field>
        <Field label="Población" htmlFor={`${idPrefix}-city`}>
          <Input id={`${idPrefix}-city`} value={values.billingCity} onChange={set("billingCity")} />
        </Field>
        <Field label="Provincia" htmlFor={`${idPrefix}-province`}>
          <Input id={`${idPrefix}-province`} value={values.billingProvince} onChange={set("billingProvince")} />
        </Field>
        <Field label="País (ISO)" htmlFor={`${idPrefix}-country`} hint="Dos letras: ES, PT…">
          <Input
            id={`${idPrefix}-country`}
            value={values.billingCountry}
            onChange={set("billingCountry")}
            maxLength={2}
            placeholder="ES"
          />
        </Field>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field
          label="Correo de facturación"
          htmlFor={`${idPrefix}-billing-email`}
          hint="Donde se envían las facturas (p. ej. el del contable). Es el que ve Stripe como correo del cliente."
        >
          <Input
            id={`${idPrefix}-billing-email`}
            type="email"
            value={values.billingEmail}
            onChange={set("billingEmail")}
            placeholder="administracion@empresa.es"
          />
        </Field>
        <Field label="Fiscalidad" htmlFor={`${idPrefix}-tax-type`} hint={TAX_TYPE_HINT}>
          <Select id={`${idPrefix}-tax-type`} value={values.taxTypeId} onChange={set("taxTypeId")}>
            <option value="">Sin impuesto</option>
            {taxTypes.map((taxType) => (
              <option key={taxType.id} value={taxType.id}>
                {taxTypeOptionLabel(taxType)}
              </option>
            ))}
          </Select>
        </Field>
      </div>
    </div>
  );
}
